import { reportError } from "utils/diagnostics";
import { ButtonText, PrimaryButton, TextButton } from "components/common/Buttons";
import { StyledTextField } from "components/common/Input";
import { PageTitle } from "components/common/Text";
import { PageSection } from "components/common/Wrapper";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import React, { BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEMO_DEVICE_CONFIG,
  DEMO_DEVICE_GRID_COLUMNS,
  DEMO_USER_CONFIG,
  DEMO_USERS_GRID_COLUMNS,
  DemoDeviceConfigTypes,
  DemoUserAddTypes,
  DemoUserColumnTypes,
  DemoUserTypes,
  DeviceListTypes,
  ListRowTypes,
  setUserIsDisabledTypes,
} from "./demo-users.config";
import { useAuth } from "hooks/useProvideAuth";
import {
  addDemoUsers,
  getDemoDevices,
  getDemoUsers,
  getUserDevices,
  setUserIsDisabled,
  deleteDemoUser,
  updateAssignedDevices,
  updateDemoDevices,
  userStatus,
} from "./demo-users.api";
import CustomTable from "components/table/table.component";
import { TableBodyCell } from "components/table/table.style";
import { getSortList, stripInvisibleChars } from "utils/util-functions";
import { Button, Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, TablePagination, TableRow } from "@mui/material";
import useConfirm from "hooks/useConfirm";
import { showError, showSuccess } from "utils/notifications";
import { Spinner } from "components/common/spinner/spinner.component";
import { Popup } from "components/common/popup/popup.component";
import { CommonText } from "components/common/text/text.component";

export default function DemoUsersPage() {
  const { sub: userId }: any = useAuth();

  const [emailList, setEmailList] = useState<string[]>([]);
  const [serialList, setSerialList] = useState<string[]>([]);
  const [demoUsersList, setDemoUsersList] = useState<DemoUserTypes[] | undefined>([]);
  const [demoDevicesList, setDemoDevicesList] = useState<DeviceListTypes[]>([]);
  const [userDevices, setUserDevices] = useState<DeviceListTypes[]>([]);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [sortOrder, setSortOrder] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [deviceSortOrder, setDeviceSortOrder] = useState<string>("");
  const [deviceSortBy, setDeviceSortBy] = useState<string>("");
  const [currentRow, setCurrentRow] = useState<ListRowTypes | null>();
  const [rowId, setRowId] = useState<number | null>(null);
  const [currentDeviceRow, setCurrentDeviceRow] = useState<DeviceListTypes | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [getConfirmation, Confirmation] = useConfirm();
  const [addDeviceDisabled, setAddDeviceDisabled] = useState<boolean>(true);
  const [addUsersDisabled, setAddUsersDisabled] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isUsersLoading, setIsUsersLoading] = useState<boolean>(true);
  const [isDevicesLoading, setIsDevicesLoading] = useState<boolean>(true);
  const [totalUserCount, setTotalUserCount] = useState<number>(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestId = useRef(0);
  const previousRefreshKey = useRef(refreshKey);

  const getUsersList = useCallback(async () => {
    const currentRequestId = requestId.current + 1;
    const refresh = previousRefreshKey.current !== refreshKey;
    requestId.current = currentRequestId;
    previousRefreshKey.current = refreshKey;
    setIsUsersLoading(true);

    try {
      const { users, totalCount } = await getDemoUsers({
        username: userId,
        rolename: "demouser",
        page,
        pageSize: rowsPerPage,
        refresh: refresh || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
      });
      if (requestId.current !== currentRequestId) return;

      setDemoUsersList(users);
      setTotalUserCount(totalCount);
    } catch (error) {
      reportError("demo_users.DemoUsersPage", error);
      if (requestId.current === currentRequestId) {
        setDemoUsersList([]);
        setTotalUserCount(0);
      }
    } finally {
      if (requestId.current === currentRequestId) {
        setIsUsersLoading(false);
      }
    }
  }, [page, refreshKey, rowsPerPage, sortBy, sortOrder, userId]);

  const refreshUsers = useCallback(() => {
    setPage(0);
    setRefreshKey((value) => value + 1);
  }, []);

  const getDevices = async () => {
    setIsDevicesLoading(true);
    try {
      const response = await getDemoDevices();
      setDemoDevicesList(response);
    } finally {
      setIsDevicesLoading(false);
    }
  };

  useEffect(() => {
    getUsersList();
  }, [getUsersList]);

  useEffect(() => {
    getDevices();
  }, []);

  const demoUsersData = useMemo(() => {
    const usersArray: any = [];

    demoUsersList?.forEach((item: any) => {
      const obj = {
        email: "",
        org: "",
        role: "",
        createdTimeStamp: item.UserCreateDate,
        expire: item.UserLastModifiedDate,
        name: item.Username,
        expirationDate: item.expirationDate,
        extend: item.Enabled,
        enabled: item.Enabled,
      };

      item.Attributes.forEach((attr: any) => {
        if (attr.Name === "email") {
          obj.email = attr.Value;
        }

        if (attr.Name === "profile") {
          obj.org = attr.Value;
        }

        if (attr.Name === "custom:Org_role") {
          obj.role = attr.Value;
        }
      });

      usersArray.push(obj);
    });

    return usersArray;
  }, [demoUsersList]);

  const visibleUsersList = demoUsersData;

  const sortedDevicesList: any[] = useMemo(() => {
    const devices = userDevices && userDevices.length ? userDevices : demoDevicesList;
    const sortColumn = DEMO_DEVICE_GRID_COLUMNS.find(({ id }) => id === deviceSortBy);
    return getSortList(devices, deviceSortBy, deviceSortOrder, sortColumn?.type);
  }, [demoDevicesList, deviceSortBy, deviceSortOrder, userDevices]);

  const emailOnChange = (e: BaseSyntheticEvent) => {
    const emailString = e.target.value.trim();
    const emailArray = emailString.split(/\s*,\s*/);

    if (emailString === "") {
      setAddUsersDisabled(true);
    } else {
      setAddUsersDisabled(false);
    }

    return setEmailList(emailArray);
  };

  const serialOnChange = (e: BaseSyntheticEvent) => {
    const serialString = e.target.value.trim();

    const serials = serialString.split(/\s*,\s*/);

    if (!serialString || !serialString.length || serialString === "") {
      setAddDeviceDisabled(true);
    } else {
      setAddDeviceDisabled(false);
    }

    return setSerialList(serials);
  };

  const onSubmitUsers = async () => {
    setIsUpdating(true);
    try {
      await Promise.all(
        emailList.map(async (rawItem: any) => {
          const item = stripInvisibleChars(rawItem);
          const user: DemoUserAddTypes = { ...DEMO_USER_CONFIG };
          const match = item.match(/^([^@]+)@/);
          if (match) {
            const [, localPart] = match;
            if (localPart) {
              user.name = localPart;
              user.username = userId;
              user.email = item;
            }
          }
          await addDemoUsers(user);
        }),
      );
      refreshUsers();
      setEmailList([]);
      setAddUsersDisabled(true);
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.onSubmitUsers", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const setUserStatus = async (row: ListRowTypes, value: string) => {
    setIsUpdating(true);
    try {
      const data: setUserIsDisabledTypes = { username: row.name, useraccount: value };
      await setUserIsDisabled(data);
      showSuccess(`Demo user ${row?.email} account status updated.`);
      refreshUsers();
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.setUserStatus", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const setUserDelete = async (row: ListRowTypes) => {
    const isConfirm = await getConfirmation({
      title: "Delete Demo User",
      content: `Are you sure that you want to delete user ${row?.email}?`,
      buttonTxt: ButtonText.Delete,
    });

    if (!isConfirm) return;

    try {
      setIsUpdating(true);
      const data: setUserIsDisabledTypes = { username: row.name, useraccount: "delete" };

      await deleteDemoUser(data);

      showSuccess(`Demo user ${row?.email} deleted.`);

      refreshUsers();
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.setUserDelete", error);
      showError(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const onSubmitDevices = async () => {
    setIsUpdating(true);
    try {
      await Promise.all(
        serialList.map(async (item: string) => {
          const device: any = { ...DEMO_DEVICE_CONFIG };
          device.serialNumber = item;
          await updateDemoDevices(device);
        }),
      );
      await getDevices();
      setAddDeviceDisabled(true);
      setSerialList([]);
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.onSubmitDevices", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getColumnValue = (row: ListRowTypes, value: any, id: string) => {
    if (id === "role") {
      if (value === "demouser") {
        return "Demo User";
      }
    }

    if (id === "createdTimeStamp") {
      const now = new Date(value);
      const formattedDate = now.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });

      const formattedTime = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

      return `${formattedDate}  ${formattedTime}`;
    }

    if (id === "extend") {
      return (
        <div>
          <TextButton onClick={() => setUserStatus(row, userStatus.extend)}>Extend</TextButton>
        </div>
      );
    }

    if (id === "enabled") {
      return (
        <div>
          <TextButton disabled={!value} onClick={() => setUserStatus(row, userStatus.disable)}>
            Disable
          </TextButton>
        </div>
      );
    }

    if (id === "delete") {
      return (
        <div>
          <TextButton
            onClick={(e: React.BaseSyntheticEvent) => {
              e.stopPropagation();
              setUserDelete(row);
            }}
          >
            Delete
          </TextButton>
        </div>
      );
    }

    return value;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPage(0);
    setRowsPerPage(newRowsPerPage);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (order: string, orderBy: string) => {
    setDeviceSortOrder(order);
    setDeviceSortBy(orderBy);
  };

  const handleUserRequestSort = (order: string, orderBy: string) => {
    setSortOrder(order);
    setSortBy(orderBy);
    setPage(0);
  };

  const onClickMenu = (event: BaseSyntheticEvent, row: any) => {
    setAnchorEl(event.currentTarget);

    setCurrentDeviceRow(row);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
    setCurrentDeviceRow(null);
  };

  const onDeleteDevice = async () => {
    setAnchorEl(null);

    const body: DemoDeviceConfigTypes = { ...DEMO_DEVICE_CONFIG };

    if (!currentDeviceRow) return;

    body.serialNumber = currentDeviceRow.serialNumber;
    body.action = "remove";

    const isConfirm = await getConfirmation({
      title: "Delete Demo Device",
      content: `Are you sure that you want to remove device ${currentDeviceRow?.serialNumber}?`,
      buttonTxt: ButtonText.Delete,
    });

    if (isConfirm) {
      setIsUpdating(true);
      try {
        const data: any = await updateDemoDevices(body);
        showSuccess(data?.message);
        await getDevices();
      } catch (error) {
        reportError("pages/demo-users/demo-users.page.onDeleteDevice", error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const onCheck = async (event: BaseSyntheticEvent, serialNumber: string) => {
    const isChecked: boolean = event.target.checked;
    setIsUpdating(true);
    try {
      const body = {
        serialNumber,
        demoUser: currentRow?.name,
        action: isChecked ? "add" : "remove",
      };
      await updateAssignedDevices(body);
      if (!currentRow) return;
      const response = await getUserDevices(currentRow?.name);
      setUserDevices(response);
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.onCheck", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getTableColumn = (row: any, id: any): any => {
    const value: any = getColumnValue(row, row[id], id);

    if (id === "assigned") {
      return (
        <Checkbox
          disabled={!currentRow}
          onChange={(e: BaseSyntheticEvent) => onCheck(e, row.serialNumber)}
          checked={row.assigned}
        />
      );
    }
    return value;
  };

  const selectCurrentRow = async (row: any, index: number) => {
    if (rowId === index) {
      setCurrentRow(null);
      setRowId(null);
      setUserDevices([]);
      return;
    }
    setCurrentRow(row);
    setRowId(index);
    setIsUpdating(true);
    try {
      const response = await getUserDevices(row.name);
      setUserDevices(response);
    } catch (error: any) {
      reportError("pages/demo-users/demo-users.page.selectCurrentRow", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <PageTitle>Demo Users & Devices</PageTitle>
      <div style={{ display: "flex", alignItems: "center" }}>
        {/* User Email Input */}
        <PageSection style={{ marginBottom: "24px", marginRight: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ paddingRight: "16px", width: "100%" }}>
              <StyledTextField value={emailList} onChange={emailOnChange} label="Email" name="emailRegister" />
            </div>
            <PrimaryButton disabled={addUsersDisabled} onClick={onSubmitUsers}>
              Add Users
            </PrimaryButton>
          </div>
          <CommonText>Note: Please separate emails with commas</CommonText>
        </PageSection>

        {/* Serial Number Input */}
        <PageSection style={{ width: "700px", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ paddingRight: "16px", width: "100%" }}>
              <StyledTextField
                value={serialList}
                multiline
                onChange={serialOnChange}
                label="Serial Numbers"
                name="serialRegister"
              />
            </div>
            <PrimaryButton disabled={addDeviceDisabled} onClick={onSubmitDevices}>
              Add Devices
            </PrimaryButton>
          </div>
          <CommonText>Note: Please separate serial numbers with commas</CommonText>
        </PageSection>
      </div>

      <div style={{ display: "flex" }}>
        <PageSection style={{ marginRight: "24px" }}>
          <CustomTable>
            <CustomTable.Header onRequestSort={handleUserRequestSort} columns={DEMO_USERS_GRID_COLUMNS} />

            <CustomTable.Body isLoading={isUsersLoading || isUpdating} deviceData={visibleUsersList}>
              {visibleUsersList.map((row: ListRowTypes, i: number) => (
                <TableRow sx={{ cursor: "pointer" }} selected={i === rowId} key={`${row.name}`}>
                  {DEMO_USERS_GRID_COLUMNS.map((column: DemoUserColumnTypes) => {
                    const value = getColumnValue(row, row[column.id as keyof ListRowTypes], column.id);

                    return (
                      <TableBodyCell
                        onClick={() => column.id !== "enabled" && selectCurrentRow(row, i)}
                        key={`${column}${column.id}`}
                        align={column.align}
                        style={{ color: "var(--mega-color-gray500)" }}
                      >
                        {column.format && typeof value === "number" ? column.format(value) : value}
                      </TableBodyCell>
                    );
                  })}
                </TableRow>
              ))}
            </CustomTable.Body>
          </CustomTable>

          <div>
            <TablePagination
              component="div"
              count={totalUserCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </PageSection>

        <PageSection style={{ width: "700px" }}>
          <CustomTable>
            <CustomTable.Header onRequestSort={handleRequestSort} columns={DEMO_DEVICE_GRID_COLUMNS} />

            <CustomTable.Body isLoading={isDevicesLoading || isUpdating} deviceData={sortedDevicesList}>
              {sortedDevicesList.map((row: any, i: number) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`${row.eiSerialNumber}${i}`}>
                    {DEMO_DEVICE_GRID_COLUMNS.map((column: any) => {
                      return (
                        <TableBodyCell
                          key={`${column}${column.id}`}
                          align={column.align}
                          style={{ color: "var(--mega-color-gray500)" }}
                        >
                          {getTableColumn(row, column.id)}
                        </TableBodyCell>
                      );
                    })}
                    <TableBodyCell style={{ width: 30 }}>
                      <Button
                        disabled={userDevices.length > 0}
                        onClick={(e) => onClickMenu(e, row)}
                        style={{ color: "var(--mega-color-gray" }}
                      >
                        <MoreVertIcon />
                      </Button>
                    </TableBodyCell>
                  </TableRow>
                );
              })}
            </CustomTable.Body>
          </CustomTable>
        </PageSection>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={onMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={onDeleteDevice}>
            <ListItemIcon>
              <DeleteOutlineIcon />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </div>
      <Confirmation />

      <Popup title="Updating data..." open={isUpdating}>
        <Spinner />
      </Popup>
    </>
  );
}
